import { useState } from 'react';
import { MessageSquare, Globe, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PublicGeniePersona } from '@/types/discovery-catalog';
import { PersonaBrand } from '@/data/genies/persona-brands';

interface PhoneMockupProps {
  genie: PublicGeniePersona;
  brand: PersonaBrand | undefined;
  logoPath: string | null;
  onClick: () => void;
  className?: string;
  variant?: 'logo' | 'text';
}

export const PhoneMockup = ({ genie, brand, logoPath, onClick, className, variant = 'logo' }: PhoneMockupProps) => {
  const [logoError, setLogoError] = useState(false);

  const primaryColor = brand?.primaryColor || '#4E9CFF';
  const secondaryColor = brand?.secondaryColor || '#1a1a2e';

  // Mix color with white for the avatar background
  const mixWithWhite = (hex: string, weight: number): string => {
    const match = hex.replace('#', '');
    const r = parseInt(match.substring(0, 2), 16) || 0;
    const g = parseInt(match.substring(2, 4), 16) || 0;
    const b = parseInt(match.substring(4, 6), 16) || 0;
    const wr = Math.round(255 * (1 - weight) + r * weight);
    const wg = Math.round(255 * (1 - weight) + g * weight);
    const wb = Math.round(255 * (1 - weight) + b * weight);
    const toHex = (v: number) => v.toString(16).padStart(2, '0');
    return `#${toHex(wr)}${toHex(wg)}${toHex(wb)}`;
  };

  // Text-mark variant needs a saturated background so the white text reads;
  // logo variant keeps the soft pale circle that the PNG sits on.
  const avatarBg = variant === 'text' ? primaryColor : mixWithWhite(primaryColor, 0.28);

  return (
    <div
      onClick={onClick}
      className={cn(
        'group cursor-pointer transition-all duration-300',
        'hover:scale-[1.03] hover:-translate-y-1',
        className
      )}
    >
      {/* Phone frame */}
      <div className="relative w-[200px] h-[410px] sm:w-[240px] sm:h-[490px] bg-gray-900 rounded-[2.5rem] border-[3px] border-gray-700 shadow-2xl overflow-hidden group-hover:border-purple-500/50 group-hover:shadow-purple-500/20 transition-all duration-300">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-900 rounded-b-2xl z-20" />

        {/* Screen */}
        <div
          className="absolute inset-[3px] rounded-[2.2rem] overflow-hidden flex flex-col"
          style={{
            background: `linear-gradient(170deg, ${primaryColor}22 0%, ${secondaryColor}44 50%, ${primaryColor}33 100%)`,
          }}
        >
          {/* Status bar */}
          <div className="h-8 flex items-end justify-center pb-0.5">
            <div className="w-12 h-1 rounded-full bg-white/20" />
          </div>

          {/* URL bar */}
          <div className="mx-4 mt-1 mb-2 flex items-center justify-center gap-1 px-3 py-1 rounded-full bg-white/10">
            <span className="text-[7px] text-white/40 truncate">
              {brand?.brandName?.toLowerCase().replace(/\s+/g, '') || 'genie'}.app
            </span>
          </div>

          {/* Main content area */}
          <div className="flex-1 flex flex-col items-center justify-center px-4 gap-2">
            {/* Status text */}
            <p className="text-[9px] sm:text-[10px] text-white/60">I'm speaking...</p>

            {/* Avatar circle */}
            <div
              className="w-[100px] h-[100px] sm:w-[120px] sm:h-[120px] rounded-full flex items-center justify-center shadow-lg"
              style={{
                background: `radial-gradient(circle at 50% 40%, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0) 58%), ${avatarBg}`,
                boxShadow: `0 0 20px 4px ${primaryColor}44, 0 0 40px 8px ${primaryColor}22`,
              }}
            >
              {variant === 'text' ? (
                <span
                  className="px-2 text-center font-bold leading-tight tracking-tight text-white drop-shadow-sm"
                  style={{
                    fontSize:
                      (brand?.brandName?.length ?? 0) > 14
                        ? '0.78rem'
                        : (brand?.brandName?.length ?? 0) > 9
                        ? '0.95rem'
                        : '1.15rem',
                    textShadow: `0 1px 8px ${secondaryColor}66`,
                  }}
                >
                  {brand?.brandName || genie.name}
                </span>
              ) : logoPath && !logoError ? (
                <img
                  src={logoPath}
                  alt={brand?.brandName || genie.name}
                  className="w-[55%] h-[55%] object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div
                  className="w-[55%] h-[55%] rounded-full"
                  style={{ background: primaryColor }}
                />
              )}
            </div>

            {/* End Conversation button */}
            <button
              className="mt-1 px-4 py-1.5 rounded-full text-[8px] sm:text-[9px] font-medium text-white"
              style={{ background: primaryColor }}
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
            >
              End Conversation
            </button>

            {/* Bottom icons */}
            <div className="flex items-center gap-3 mt-1">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full border border-white/20 flex items-center justify-center">
                <MessageSquare className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white/50" />
              </div>
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full border border-white/20 flex items-center justify-center">
                <Globe className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white/50" />
              </div>
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full border border-white/20 flex items-center justify-center">
                <Info className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white/50" />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="h-5 flex items-center justify-center">
            <div className="w-24 h-1 rounded-full bg-white/20" />
          </div>
        </div>
      </div>
    </div>
  );
};
